import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useQueryClient } from 'react-query';

import {
  QBadge,
  QButton,
  QButtonGroup,
  QFlex,
  QHeading,
  QLink,
  QStack,
  QBox,
  QTag,
  QText,
  QTogglePropertiesPanelButton,
  useCurrentUser,
} from '@qualio/ui-components';

import { StatusTag } from '../../components';
import {
  EventTag,
  EventTemplateFieldAttributeNames,
  IntegrationFields,
  QualityEventDetailsResponse,
} from '../../types';

import { useLocalisedFormatDateTime } from '../../utils/datetimeUtils';
import { isFieldVisible } from '../../utils/eventTemplateUtils';
import { useEventPermissions, useUpdateDueDate } from '../../hooks';
import { useFlags } from '../../external/LaunchDarklyAdapter';
import ChangeDueDateModal from '../ChangeDueDateModal/ChangeDueDateModal';
import { ChangeDueDateForm } from '../ChangeDueDateModal/formHelpers';
import IntegrationVariablesDrawer from './components/IntegrationVariablesDrawer';
import TagsSection from './components/TagsSection';
import { ManageCollaborators } from './components/ManageCollaborators/ManageCollaborators';
import { EscalationDetails } from '../../types/escalationDetails';
import { PropertySection } from './components/PropertySection';
import { PropertySectionText } from './components/PropertySectionText';
import { stripQualioHost, toAbsoluteUrl } from '../../utils/UrlUtils';
import EventActionLog from './components/EventActionLog';
import { EventActionLog as EventActionLogType } from '../../types/eventActionLog';
import { hasCodeProperty, hasNameProperty, QriDetailsV2 } from '@qualio/search-qri';
import { QESourceComponent } from '../QESourceComponent/QESourceComponent';
import { DateTime } from 'luxon';

type Props = {
  event: QualityEventDetailsResponse;
  relatedSuppliers: QriDetailsV2[] | undefined;
  relatedDocuments: QriDetailsV2[] | undefined;
  relatedEvents: QriDetailsV2[] | undefined;
  tags: EventTag[] | undefined;
  eventActionLog: EventActionLogType | undefined;
};

const EventProperties: React.FC<Props> = ({
  event,
  relatedSuppliers,
  relatedEvents,
  relatedDocuments,
  tags,
  eventActionLog,
}) => {
  const { t } = useTranslation();
  const { formatDate, companyId } = useCurrentUser();

  const issue = event.issue;
  const [isIntegrationFieldsVisible, setIsIntegrationFieldsVisible] = useState(false);
  const [isChangeDueDateModalOpen, setIsChangeDueDateModalOpen] = useState(false);

  const isEventCollaboratorsEnabled = useFlags('eventCollaborators');
  const { canChangeDueDate } = useEventPermissions();
  const queryClient = useQueryClient();
  const { updateDueDate, isLoading: isUpdateDueDateLoading } = useUpdateDueDate(issue.id);

  const handleChangeDueDate = async (payload: ChangeDueDateForm) => {
    await updateDueDate(payload);
    await queryClient.invalidateQueries(['eventDetails', companyId, issue.id]);
  };

  let issueTags: EventTag[] = [];
  const formatDateTime = useLocalisedFormatDateTime();

  const visibleFields = useMemo(() => {
    const visibleFields = new Set<string>();
    for (const field in EventTemplateFieldAttributeNames.enum) {
      if (isFieldVisible(event?.issue?.template_fields, EventTemplateFieldAttributeNames.parse(field))) {
        visibleFields.add(field);
      }
    }
    return visibleFields;
  }, [event]);

  if (tags && issue.tags_ids) {
    const ids = issue.tags_ids;
    issueTags = tags.filter((tag) => ids.includes(Number(tag.id)));
  }

  const createIntegrationVariablesSection = (fields: IntegrationFields) => {
    const variablesCount = Object.keys(fields).length;
    return (
      <>
        <PropertySection title={t('common.labels.integrationVariables')}>
          <button onClick={() => setIsIntegrationFieldsVisible(true)}>
            <QText color="blue.500">{variablesCount} variables</QText>
          </button>
        </PropertySection>
        <IntegrationVariablesDrawer
          fields={fields}
          isOpen={isIntegrationFieldsVisible}
          onClose={() => setIsIntegrationFieldsVisible(false)}
        />
      </>
    );
  };

  const createRelatedEntitiesBadges = (entities: QriDetailsV2[]) => {
    if (!entities || entities.length === 0) {
      return <QText>-</QText>;
    }
    return (
      <QFlex flexWrap="wrap">
        {entities.map((entity: QriDetailsV2) => {
          const {
            domain,
            urls: { view },
          } = entity;
          const badgeCode =
            hasCodeProperty(entity) && entity.identifiers.code
              ? entity.identifiers.code
              : String(entity.identifiers.id);
          const copyValue = view ? toAbsoluteUrl(view) : badgeCode;
          return (
            <QBadge mr={2} mb={2} key={badgeCode} textTransform="none" copyValue={copyValue}>
              {view && hasNameProperty(entity) ? (
                <Link
                  color="blue.500"
                  data-metrics={`event-details-related-${domain}-link`}
                  to={view}
                  title={entity.name}
                >
                  {entity.identifiers.code}
                </Link>
              ) : (
                <>{badgeCode}</>
              )}
            </QBadge>
          );
        })}
      </QFlex>
    );
  };

  const createEscalationLinks = (details: EscalationDetails[]) => {
    if (details.length === 0) {
      return <QText>-</QText>;
    }
    return (
      <QFlex flexWrap="wrap">
        {details.map(({ id, code, title }) => (
          <QBadge
            mr={2}
            mb={2}
            key={code}
            textTransform="none"
            copyValue={toAbsoluteUrl(`/quality-events/events/${id}`)}
          >
            <Link
              color="blue.500"
              data-metrics={`event-details-escalation-${id}-link`}
              to={`/events/${id}`}
              title={title}
            >
              {code}
            </Link>
          </QBadge>
        ))}
      </QFlex>
    );
  };

  const createSupplierLinks = (suppliers: QriDetailsV2[]): JSX.Element => {
    if (!suppliers || suppliers.length === 0) {
      return <QText>-</QText>;
    }
    const links = suppliers.reduce(
      (result, supplier, index, array) => {
        if (hasNameProperty(supplier)) {
          result.push(
            supplier.urls.view ? (
              <QLink key={supplier.identifiers.id} href={stripQualioHost(supplier.urls.view)} isCrossMFE>
                {supplier.name}
              </QLink>
            ) : (
              <QText key={supplier.identifiers.id}>{supplier.name}</QText>
            ),
          );
        }

        if (index < array.length - 1) {
          result.push(', ');
        }
        return result;
      },
      [] as (JSX.Element | string)[],
    );
    return <QText>{links}</QText>;
  };

  const createProductRootCauseLink = (entityType: 'product' | 'rootCause') => {
    const products = issue.products;
    const rootCause = issue.rootcauses;
    const entities = { product: products, rootCause: rootCause }[entityType];
    if (entities?.length === 0) {
      return <QText key={entityType}>-</QText>;
    }
    const links = entities?.map((entity, index) => {
      return (
        <Link key={entity.qri} to={entity.qri}>
          {entity.name}
          {index < entities?.length - 1 && ', '}
        </Link>
      );
    });
    return <QText>{links}</QText>;
  };

  const riskSection = useMemo(() => {
    const issueRisk = issue.risk ? <QTag>{issue.risk}</QTag> : <PropertySectionText>{'-'}</PropertySectionText>;
    return visibleFields.has(EventTemplateFieldAttributeNames.enum.risk) ? (
      <PropertySection title={t('common.labels.risk')}>{issueRisk}</PropertySection>
    ) : null;
  }, [visibleFields, issue, t]);

  const shouldShowEventActionLog = useCallback(
    (issueStatus: string, selectedActionLog: EventActionLogType): boolean => {
      if (['cancelled', 'rejected'].includes(issueStatus) && selectedActionLog) {
        return true;
      }
      // This logic is needed because closed and closed early have same status
      return issueStatus === 'closed' && selectedActionLog && selectedActionLog.destination_status === 'closed';
    },
    [],
  );

  const due = issue.due_date
    ? formatDate(DateTime.fromFormat(issue.due_date, 'yyyy-MM-dd', { zone: 'utc' }).toString())
    : formatDateTime(issue.due_at);

  return (
    <QBox maxWidth={'300px'}>
      <QStack
        marginTop={4}
        marginBottom={4}
        direction={'row'}
        justifyContent="space-between"
        spacing="8px"
        alignItems="center"
      >
        <QHeading size={'sm'}>{t('common.labels.properties')}</QHeading>
        <QButtonGroup>
          <QTogglePropertiesPanelButton />
        </QButtonGroup>
      </QStack>
      {/* check for undefined in minor version specifically since minor can be 0 */}
      {issue.workflow_major_version && issue.workflow_minor_version !== undefined && (
        <PropertySection title={t('common.labels.eventType')}>
          <QText>{`${issue.workflow} (v${issue.workflow_major_version}.${issue.workflow_minor_version})`}</QText>
        </PropertySection>
      )}

      <PropertySection title={t('common.labels.status')}>
        <StatusTag status={issue.status} />
      </PropertySection>
      {eventActionLog && shouldShowEventActionLog(issue.status, eventActionLog) ? (
        <EventActionLog eventStatus={issue.status} eventActionLog={eventActionLog} />
      ) : null}
      <PropertySection
        title={t('common.labels.due')}
        action={
          canChangeDueDate && (
            <QButton
              variant="link"
              size="xs"
              onClick={() => setIsChangeDueDateModalOpen(true)}
              data-cy="change-due-date-button"
            >
              {t('common.actions.change')}
            </QButton>
          )
        }
      >
        <PropertySectionText>{due}</PropertySectionText>
      </PropertySection>
      <PropertySection title={t('common.labels.lastModified')}>
        <PropertySectionText>{formatDateTime(issue.updated_at)}</PropertySectionText>
      </PropertySection>
      <PropertySection title={t('common.labels.owner')}>
        <PropertySectionText>{issue.owner}</PropertySectionText>
      </PropertySection>
      {isEventCollaboratorsEnabled && <ManageCollaborators event={issue} />}
      <PropertySection title={t('common.labels.created')}>
        <PropertySectionText>{`${formatDateTime(issue.created_at)} by ${issue.creator}`}</PropertySectionText>
      </PropertySection>
      <PropertySection title={t('events.labels.relatedEvents')}>
        {createRelatedEntitiesBadges(relatedEvents ?? [])}
      </PropertySection>
      <PropertySection title={t('events.labels.relatedDocuments')}>
        {createRelatedEntitiesBadges(relatedDocuments ?? [])}
      </PropertySection>
      <PropertySection title={t('events.labels.sourceEvent')}>
        {createEscalationLinks(event.issue.escalated_from ?? [])}
      </PropertySection>
      <PropertySection title={t('events.labels.escalatedTo')}>
        {createEscalationLinks(event.issue.escalated_to ?? [])}
      </PropertySection>
      {visibleFields.has(EventTemplateFieldAttributeNames.enum.supplier) ? (
        <PropertySection title={t('events.labels.relatedSuppliers')}>
          {createSupplierLinks(relatedSuppliers ?? [])}
        </PropertySection>
      ) : null}
      {riskSection}
      {visibleFields.has(EventTemplateFieldAttributeNames.enum.risk) ? (
        <PropertySection title={t('common.labels.severity')}>
          <PropertySectionText>{issue.severity ?? '-'}</PropertySectionText>
        </PropertySection>
      ) : null}
      {visibleFields.has(EventTemplateFieldAttributeNames.enum.risk) ? (
        <PropertySection title={t('common.labels.probability')}>
          <PropertySectionText>{issue.probability ?? '-'}</PropertySectionText>
        </PropertySection>
      ) : null}
      {visibleFields.has(EventTemplateFieldAttributeNames.enum.product) ? (
        <PropertySection title={t('common.labels.product')} dataCySuffix="registry">
          {createProductRootCauseLink('product')}
        </PropertySection>
      ) : null}
      {visibleFields.has(EventTemplateFieldAttributeNames.enum.root_cause) ? (
        <PropertySection title={t('common.labels.rootCause')} dataCySuffix="registry">
          {createProductRootCauseLink('rootCause')}
        </PropertySection>
      ) : null}
      {issue.qe_source ? (
        <PropertySection title={t('common.labels.source')}>
          <QESourceComponent source={issue.qe_source} />
        </PropertySection>
      ) : null}
      {issue.fields && createIntegrationVariablesSection(issue.fields)}
      <TagsSection tags={issueTags} />
      <ChangeDueDateModal
        isOpen={isChangeDueDateModalOpen}
        onClose={() => setIsChangeDueDateModalOpen(false)}
        onSubmit={handleChangeDueDate}
        isLoading={isUpdateDueDateLoading}
        currentDueDate={due}
        eventName={issue.title}
      />
    </QBox>
  );
};

export default EventProperties;
